(() => {
  const DATA_SELECTOR = "[data-sw-swatch-json]";
  const PRODUCT_ROOT_SELECTOR = "[data-sw-product-root]";
  const VISUAL_LABEL_SELECTOR = ".swp__option-label--visual";

  const observers = new WeakMap();
  const scheduledRoots = new WeakSet();

  /**
   * Normalize text so capitalization and extra spaces
   * do not prevent an option match.
   */
  function normalize(value) {
    return String(value || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  function makeKey(optionName, optionValue) {
    return `${normalize(optionName)}\u0000${normalize(optionValue)}`;
  }

  /**
   * Read the JSON generated by the hidden Liquid section.
   */
  function readSwatchEntries() {
    const entries = [];

    document
      .querySelectorAll(DATA_SELECTOR)
      .forEach((script) => {
        try {
          const parsed = JSON.parse(
            script.textContent || "[]"
          );

          if (Array.isArray(parsed)) {
            entries.push(...parsed);
          }
        } catch (error) {
          console.error(
            "Slickwraps global swatch data could not be parsed.",
            error
          );
        }
      });

    return entries.filter(
      (entry) =>
        entry &&
        entry.optionName &&
        entry.optionValue &&
        entry.src
    );
  }

  /**
   * Build an exact Option Name + Option Value lookup.
   *
   * Example:
   * Camo + Orange
   * Camo + Blue
   * Camo + Desert
   */
  function buildSwatchIndex(entries) {
    const index = new Map();

    entries.forEach((entry) => {
      const key = makeKey(
        entry.optionName,
        entry.optionValue
      );

      index.set(key, entry);
    });

    return index;
  }

  function getOptionName(label) {
    const fieldset = label.closest(
      ".swp__option-group"
    );

    const optionName =
      fieldset?.dataset.swOptionName ||
      fieldset
        ?.querySelector(".swp__option-name")
        ?.textContent ||
      fieldset
        ?.querySelector("legend span")
        ?.textContent;

    return normalize(optionName);
  }

  function getOptionValue(label) {
    const inputId = label.getAttribute("for");

    if (inputId) {
      const input = document.getElementById(inputId);

      if (input?.value) {
        return normalize(input.value);
      }
    }

    const hiddenText = label
      .querySelector(
        ".swp__sr-only, .visually-hidden"
      )
      ?.textContent;

    if (hiddenText) {
      return normalize(hiddenText);
    }

    const title = label.getAttribute("title") || "";

    return normalize(
      title
        .split("—")[0]
        .trim()
    );
  }

  function restoreSourceImage(
    swatch,
    image
  ) {
    if (
      !swatch ||
      !image ||
      image.dataset
        .swReusedSource !== "true"
    ) {
      return false;
    }

    const sourceSrc =
      swatch.dataset.swSourceSrc || "";

    const sourceSrcset =
      swatch.dataset.swSourceSrcset || "";

    const sourceSizes =
      swatch.dataset.swSourceSizes || "";

    if (sourceSrcset) {
      image.setAttribute(
        "srcset",
        sourceSrcset
      );
    } else {
      image.removeAttribute(
        "srcset"
      );
    }

    if (sourceSizes) {
      image.setAttribute(
        "sizes",
        sourceSizes
      );
    } else {
      image.removeAttribute(
        "sizes"
      );
    }

    if (sourceSrc) {
      image.setAttribute(
        "src",
        sourceSrc
      );
    } else {
      image.removeAttribute(
        "src"
      );
    }

    image.classList.remove(
      "swp__option-dedicated-image",
      "is-swatch-loaded"
    );

    image.removeAttribute(
      "aria-hidden"
    );

    image.removeAttribute(
      "fetchpriority"
    );

    delete image.dataset
      .swReusedSource;

    delete image.dataset
      .swatchOption;

    delete image.dataset
      .swatchValue;

    delete swatch.dataset
      .swSourceImage;

    delete swatch.dataset
      .swSourceSrc;

    delete swatch.dataset
      .swSourceSrcset;

    delete swatch.dataset
      .swSourceSizes;

    return true;
  }

  function removeDedicatedSwatch(swatch) {
    if (!swatch) return;

    const dedicatedImage = swatch.querySelector(
      ".swp__option-dedicated-image"
    );

    if (
      dedicatedImage &&
      !restoreSourceImage(
        swatch,
        dedicatedImage
      )
    ) {
      dedicatedImage.remove();
    }

    swatch.classList.remove(
      "has-dedicated-swatch"
    );
  }

  /**
   * Reuse the original option image for the dedicated swatch.
   *
   * Its original URL is retained as lightweight data so gallery
   * thumbnails can continue selecting the matching variant.
   */
  function installDedicatedSwatch(
    swatch,
    entry,
    optionName,
    optionValue,
    loadEagerly = false
  ) {
    let dedicatedImage = swatch.querySelector(
      ".swp__option-dedicated-image"
    );

    if (!dedicatedImage) {
      const originalImage =
        swatch.querySelector("img");

      if (originalImage) {
        const sourceImage =
          originalImage.currentSrc ||
          originalImage.getAttribute(
            "src"
          ) ||
          "";

        swatch.dataset.swSourceImage =
          sourceImage;

        swatch.dataset.swSourceSrc =
          originalImage.getAttribute(
            "src"
          ) ||
          "";

        swatch.dataset.swSourceSrcset =
          originalImage.getAttribute(
            "srcset"
          ) ||
          "";

        swatch.dataset.swSourceSizes =
          originalImage.getAttribute(
            "sizes"
          ) ||
          "";

        dedicatedImage =
          originalImage;

        dedicatedImage.classList.add(
          "swp__option-dedicated-image"
        );

        dedicatedImage.dataset
          .swReusedSource = "true";
      } else {
        dedicatedImage =
          document.createElement(
            "img"
          );

        dedicatedImage.className =
          "swp__option-dedicated-image";

        dedicatedImage.width = 320;
        dedicatedImage.height = 320;

        swatch.appendChild(
          dedicatedImage
        );
      }

    }

    dedicatedImage.alt = "";
    dedicatedImage.loading = loadEagerly
      ? "eager"
      : "lazy";
    dedicatedImage.decoding = "async";
    dedicatedImage.draggable = false;

    dedicatedImage.setAttribute(
      "aria-hidden",
      "true"
    );

    dedicatedImage.setAttribute(
      "fetchpriority",
      "low"
    );

    dedicatedImage.classList.remove(
      "is-swatch-loaded"
    );

    const revealDedicatedImage = () => {
      if (
        dedicatedImage.classList.contains(
          "swp__option-dedicated-image"
        )
      ) {
        dedicatedImage.classList.add(
          "is-swatch-loaded"
        );
      }
    };

    dedicatedImage.addEventListener(
      "load",
      revealDedicatedImage,
      { once: true }
    );

    dedicatedImage.addEventListener(
      "error",
      () => removeDedicatedSwatch(swatch),
      { once: true }
    );

    if (entry.srcset) {
      dedicatedImage.srcset =
        entry.srcset;

      dedicatedImage.sizes =
        "48px";
    } else {
      dedicatedImage.removeAttribute(
        "srcset"
      );

      dedicatedImage.removeAttribute(
        "sizes"
      );
    }

    if (
      dedicatedImage.getAttribute(
        "src"
      ) !== entry.src
    ) {
      dedicatedImage.src =
        entry.src;
    }

    if (
      dedicatedImage.complete &&
      dedicatedImage.naturalWidth > 0
    ) {
      revealDedicatedImage();
    }

    dedicatedImage.dataset.swatchOption =
      optionName;

    dedicatedImage.dataset.swatchValue =
      optionValue;

    swatch.classList.add(
      "has-dedicated-swatch"
    );
  }

  /**
   * Apply global images to all matching visual options.
   */
  function applyDedicatedSwatches(root, index) {
    if (!root) return;

    const firstVisibleLabels = new Set();

    root
      .querySelectorAll(
        ".swp__option-values--visual"
      )
      .forEach((track) => {
        const firstVisibleLabel =
          Array.from(
            track.querySelectorAll(
              VISUAL_LABEL_SELECTOR
            )
          ).find(
            (label) => !label.hidden
          );

        if (firstVisibleLabel) {
          firstVisibleLabels.add(
            firstVisibleLabel
          );
        }
      });

    root
      .querySelectorAll(VISUAL_LABEL_SELECTOR)
      .forEach((label) => {
        const optionName = getOptionName(label);
        const optionValue = getOptionValue(label);

        if (!optionName || !optionValue) {
          return;
        }

        const swatch = label.querySelector(
          ".swp__option-swatch"
        );

        if (!swatch) return;

        const inputId =
          label.getAttribute("for");

        const input = inputId
          ? document.getElementById(
              inputId
            )
          : null;

        const loadEagerly =
          firstVisibleLabels.has(label) ||
          Boolean(
            input &&
              root.contains(input) &&
              input.checked
          );

        const key = makeKey(
          optionName,
          optionValue
        );

        const entry = index.get(key);

        if (!entry?.src) {
          removeDedicatedSwatch(swatch);
          return;
        }

        installDedicatedSwatch(
          swatch,
          entry,
          optionName,
          optionValue,
          loadEagerly
        );
      });
  }

  function scheduleApply(root, index) {
    if (
      !root ||
      scheduledRoots.has(root)
    ) {
      return;
    }

    scheduledRoots.add(root);

    window.requestAnimationFrame(() => {
      scheduledRoots.delete(root);

      applyDedicatedSwatches(
        root,
        index
      );
    });
  }

  /**
   * The custom product section replaces the right-side product
   * information after a variant change. Watch for that replacement
   * and immediately reinstall the dedicated swatches.
   */
  function observeProductRoot(root, index) {
    if (
      !root ||
      observers.has(root)
    ) {
      return;
    }

    const observer = new MutationObserver(
      (mutations) => {
        const markupChanged = mutations.some(
          (mutation) =>
            mutation.type === "childList" &&
            (
              mutation.addedNodes.length > 0 ||
              mutation.removedNodes.length > 0
            )
        );

        if (markupChanged) {
          scheduleApply(root, index);
        }
      }
    );

    observer.observe(root, {
      childList: true,
      subtree: true
    });

    observers.set(root, observer);
  }

  function initializeGlobalSwatches() {
    const entries = readSwatchEntries();

    if (!entries.length) {
      console.info(
        "No matching active Slickwraps global swatches were found for this product."
      );

      return;
    }

    const index = buildSwatchIndex(entries);

    document
      .querySelectorAll(PRODUCT_ROOT_SELECTOR)
      .forEach((root) => {
        applyDedicatedSwatches(root, index);
        observeProductRoot(root, index);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initializeGlobalSwatches,
      {
        once: true
      }
    );
  } else {
    initializeGlobalSwatches();
  }

  document.addEventListener(
    "shopify:section:load",
    initializeGlobalSwatches
  );

  window.addEventListener(
    "pageshow",
    initializeGlobalSwatches
  );
})();
